import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import useClassNames from '@loomstateorg/shared-frontend/hooks/useClassNames';
import useOnClickOutside from '@loomstateorg/shared-frontend/hooks/useOnClickOutside';
import useScrollBlock from '@loomstateorg/shared-frontend/hooks/useScrollBlock';
import Overlay from '@loomstateorg/shared-frontend/components/commercetools-ui/atoms/overlay';
export interface DrawerProps {
  children?: React.ReactNode;

  className?: string;
  isOpen: boolean;
  direction: 'left' | 'top' | 'right' | 'bottom' | 'under';
  blockScrolling?: boolean;
  onClose?: () => void;
}

const Drawer: FC<DrawerProps> = ({
  className = '',
  isOpen,
  direction = 'right',
  blockScrolling = true,
  onClose,
  children,
}) => {
  const { blockScroll } = useScrollBlock();

  useEffect(() => {
    if (blockScrolling) {
      blockScroll(isOpen);
    }
  }, [isOpen, blockScroll, blockScrolling]);

  const ref = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, () => {
    !!isOpen && onClose?.();
  });

  const directionStyles = {
    left: 'left-0 top-0 h-full',
    top: 'left-0 top-0 w-full',
    right: 'right-0 top-0 h-full',
    bottom: 'left-0 bottom-0 w-full',
    under: 'fixed top-0 left-0 right-0 w-fit mx-auto mt-30 bg-site',
  };

  const getTransitionStyles = useCallback(() => {
    if (isOpen) return 'opacity-1';
    else {
      const transitionStyles = {
        left: '-translate-x-full opacity-0',
        top: '-translate-y-full opacity-0',
        right: 'translate-x-full opacity-0',
        bottom: 'translate-y-full opacity-0',
        under: 'translate-y-full opacity-0',
      };

      return transitionStyles[direction];
    }
  }, [isOpen, direction]);

  const [transitionClassNames, setTransitionClassNames] = useState('');

  const drawerClassName = useClassNames([
    'bg-site',
    className,
    'fixed z-[999] shadow-lg',
    directionStyles[direction],
    getTransitionStyles(),
    transitionClassNames,
  ]);

  useEffect(() => {
    setTransitionClassNames('transition duration-300 ease-out');
  }, []);

  return (
    <>
      {isOpen && <Overlay showSpinner={false} />}

      <div ref={ref} data-component-group="text-container" data-testid="atom-drawer" style={{ display: isOpen ? 'block' : 'none' }} className={drawerClassName}>
        <div className="flex h-full flex-col items-stretch">{children}</div>
      </div>
    </>
  );
};

export default Drawer;
